import { Form, Modal, Row, Table } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';
import { IAmlz, Ic2Prehled } from 'types/amlz';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import Paragraph from 'components/atoms/Paragraph';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import { IcisStaty } from 'services/cisStaty';
import {
    amlzC2osobyDetailGet,
    amlzC2osobyDetailsend,
    amlzC2osobyPridat,
    platebniInstrukcePDFapi
} from 'services/spravaEmitenta/amlzData';
import { EStavZadostAmlz } from 'services/spravaEmitenta/historickeZadosti';
import {
    ISubjPravo,
    Iosoba,
    biAmlzRerouteApi,
    osobyC2Odebrat,
    pdfBiRerouteApi,
    pdfPodpisApi
} from 'services/spravaEmitenta/zadostPravo';

import { ENotificationType } from 'enums/notificationType';

import getLanguageObject from 'utils/getLanguageObject';

import AgendaAmlz from '../../agendaAmlz/formular/AgendaAmlzFormular';

interface IC2 {
    C2data?: Ic2Prehled;
    countries?: IcisStaty[];
    auth: string | null;
    nextStep: () => void;
    prevStep: () => void;
    SubmitFunc: <T extends keyof IAmlz['amlz']>(key: T, values: IAmlz['amlz'][T]) => void;
    isC6edit: string;
    idZadosti: number;
    setRefetchC2: React.Dispatch<React.SetStateAction<boolean>>;
    isLegalEntity: boolean;
}

const C2: React.FC<IC2> = ({
    C2data,
    nextStep,
    prevStep,
    SubmitFunc,
    isC6edit,
    idZadosti,
    setRefetchC2,
    isLegalEntity
}) => {
    const { confirm } = Modal;
    const [form] = Form.useForm();
    const { language, profily, NotificationComponent, amlz } = useContext(Context);
    const [personData, setPersonData] = useState<ISubjPravo | undefined>();
    const [submitPersonData, setSubmitPersonData] = useState<ISubjPravo | null>();
    const [personDataFinish, setPersonDataFinish] = useState<boolean>(false);
    const [subjIndex, setSubjIndex] = useState<number>(-1);
    const [showInstrukceAfterFinish, setshowInstrukceAfterFinish] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [putResp, setPutResp] = useState<string>('');
    const [isSubmittingManually, setIsSubmittingManually] = useState<boolean>(false);
    const c2ChybaVse = !C2data?.vse;
    const OsobyC2data = C2data?.osoby;
    const OsobyC2dataFiltered = C2data?.osoby.filter(
        e => e.stav !== EStavZadostAmlz.ODEBRAT && e.stav !== EStavZadostAmlz.PRIDAT
    );
    const idOsoby =
        subjIndex > -1 && OsobyC2data?.length && OsobyC2data[subjIndex]
            ? OsobyC2data[subjIndex].idosoby || OsobyC2data[subjIndex].id
            : amlz?.goToC2Osoba || null;
    const OsobyC2pridatData = C2data?.osoby.filter(e => e.stav === EStavZadostAmlz.PRIDAT);

    const apiListZadostC2 = (idZadost: number | string, idOsoba: number | string, personObject: ISubjPravo) => {
        return {
            downloadSignature: () =>
                amlzC2osobyDetailsend(idZadost, idOsoba, personObject, 'PUT').then(data => {
                    setPutResp(data.stav === 'OK' ? '' : data.stav);
                    if (data.stav === 'OK') {
                        pdfPodpisApi(idZadost!);
                    }
                }),
            pdfBiReroute: () =>
                amlzC2osobyDetailsend(idZadost, idOsoba, personObject, 'PUT').then(data => {
                    setPutResp(data.stav === 'OK' ? '' : data.stav);
                    if (data.stav === 'OK') {
                        return pdfBiRerouteApi(idZadost || 0, language);
                    }
                }),
            biReroute: () =>
                amlzC2osobyDetailsend(idZadost, idOsoba, personObject, 'PUT').then(data => {
                    setPutResp(data.stav === 'OK' ? '' : data.stav);
                    if (data.stav === 'OK') {
                        return biAmlzRerouteApi(idZadost!, language);
                    }
                })
        };
    };

    const onFinish = () => {
        nextStep();
    };

    useEffect(() => {
        if (amlz?.goToC2Osoba) {
            setLoading(true);
            amlzC2osobyDetailGet(idZadosti, amlz?.goToC2Osoba)
                .then(resp => setPersonData(resp))
                .finally(() => setLoading(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [amlz?.goToC2Osoba]);

    useEffect(() => {
        setRefetchC2(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // submit pri C2pridatOsobu
        if (submitPersonData) {
            const isPost = submitPersonData.doplneno;
            setLoading(true);
            amlzC2osobyDetailsend(idZadosti, idOsoby!, submitPersonData, isPost && personDataFinish ? 'POST' : 'PUT')
                .then(data => {
                    data && setPutResp(data.stav === 'OK' ? '' : data.stav);
                    if (
                        (personDataFinish ||
                            submitPersonData.stav === EStavZadostAmlz.UZIVEMAIL ||
                            isSubmittingManually) &&
                        data.stav === 'OK'
                    ) {
                        if (isSubmittingManually) {
                            NotificationComponent({
                                type: ENotificationType.SUCCESS,
                                //   title: 'IDENTIFIKACE_OSOBY',
                                description: 'AMLZ_C2_PUT_SUCCESS'
                            });
                            setIsSubmittingManually(false);
                        }
                        window.scrollTo(0, 0);
                        setSubjIndex(-1);
                        setPersonData(undefined);
                        setSubmitPersonData(null);
                        setPersonDataFinish(false);
                        setRefetchC2(pv => !pv);
                    } else {
                        amlzC2osobyDetailGet(idZadosti, idOsoby!).then(resp => {
                            setPersonData(resp);
                            if (showInstrukceAfterFinish) {
                                setLoading(true);
                                platebniInstrukcePDFapi(idZadosti, language).finally(() => setLoading(false));
                                setshowInstrukceAfterFinish(false);
                            }
                        });
                    }
                })
                .finally(() => {
                    setLoading(false);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitPersonData]);

    const getColumns = (isPridat?: boolean) => [
        {
            title: <TranslateText>JMENO</TranslateText>,
            dataIndex: 'jmeno',
            key: 'jmeno',
            render: (_text: Iosoba, row: Iosoba) => (
                <p
                    style={{
                        margin: 0
                    }}
                >
                    {(row.jmeno || '') +
                        ' ' +
                        (row.prijmeni || '') +
                        (isPridat ? ' ' + row.email : '') +
                        (row.duplicita
                            ? ' (' + getLanguageObject(language).AMLZ_C2_DUPLICITA + ' ' + row.duplicita + ')'
                            : '')}
                </p>
            )
        },
        ...(!isPridat
            ? [
                  {
                      title: (
                          <>
                              <TranslateText>STAV</TranslateText>
                          </>
                      ),
                      dataIndex: 'stav',
                      key: 'stav',
                      render: (text: string, row: Iosoba) => (
                          <>
                              <TranslateText>
                                  {text
                                      ? `STAVZADOSTI_SIMPLE_${
                                            text === EStavZadostAmlz.CDCPKEKONTR ? 'CDCPKEKONTR_ALT' : text
                                        }`
                                      : ''}
                              </TranslateText>{' '}
                              {row.datum &&
                                  (text === EStavZadostAmlz.UZIVCEKA || text === EStavZadostAmlz.UZIVDOPL) &&
                                  `(${
                                      text === EStavZadostAmlz.UZIVCEKA
                                          ? getLanguageObject(language).ODESLANO + ' '
                                          : text === EStavZadostAmlz.UZIVDOPL
                                            ? getLanguageObject(language).EMISE_PODZAD_STAV_DOPLENO + ' '
                                            : ''
                                  }` +
                                      dayjs(row.datum).format('DD.MM.YYYY HH:mm') +
                                      ')'}
                          </>
                      )
                  }
              ]
            : []),
        ...(isPridat
            ? [
                  {
                      width: 100,
                      render: (_text: Iosoba, row: Iosoba, index: number) => (
                          <div
                              style={{
                                  display: 'flex',
                                  justifyContent: 'right'
                              }}
                          >
                              <AnchorComponent
                                  key={index}
                                  onClick={() => {
                                      setLoading(true);
                                      amlzC2osobyDetailGet(idZadosti, row.idosoby!)
                                          .then(resp => {
                                              const updateData = {
                                                  ...resp,
                                                  stav: EStavZadostAmlz.ROZP
                                              };
                                              amlzC2osobyDetailsend(idZadosti, row.idosoby!, updateData, 'PUT');
                                              setPersonData(updateData);
                                          })
                                          .finally(() => setLoading(false));
                                      OsobyC2data?.map((e, i) => e.idosoby === row.idosoby && setSubjIndex(i));
                                  }}
                                  id={`c2OpridatBtn${index}`}
                              >
                                  <TranslateText>PRIDAT</TranslateText>
                              </AnchorComponent>{' '}
                              {row.id + '' !== profily?.id + '' && (
                                  <AnchorComponent
                                      style={{
                                          marginLeft: 14
                                      }}
                                      key={`c2OderbratBtn2${index}`}
                                      onClick={() => {
                                          confirm({
                                              title: <TranslateText>POTVRDENI</TranslateText>,
                                              content: `${getLanguageObject(language).DELETE_CONFIRM_C2_TEXT}`,
                                              cancelText: <TranslateText>NE</TranslateText>,
                                              okText: <TranslateText>ANO</TranslateText>,
                                              onOk() {
                                                  setLoading(true);
                                                  setSubjIndex(-1);
                                                  osobyC2Odebrat(row.idosoby!, row.idzadosti!)
                                                      .then(() => setRefetchC2(pv => !pv))
                                                      .finally(() => setLoading(false));
                                              }
                                          });
                                      }}
                                      id={`c2OderbratBtn2${index}`}
                                  >
                                      <TranslateText>ODEBRAT</TranslateText>
                                  </AnchorComponent>
                              )}
                          </div>
                      )
                  }
              ]
            : [
                  {
                      render: (_text: Iosoba, row: Iosoba, index: number) => (
                          <div
                              style={{
                                  display: 'flex',
                                  justifyContent: 'right'
                              }}
                          >
                              {
                                  <AnchorComponent
                                      id={`c2ZobrazitBtn${index}`}
                                      key={`c2ZobrazitBtn${index}`}
                                      onClick={() => {
                                          setLoading(true);
                                          amlzC2osobyDetailGet(idZadosti, row.idosoby!)
                                              .then(resp => {
                                                  if (row.stav === EStavZadostAmlz.INI) {
                                                      const updateData = {
                                                          ...resp,
                                                          stav: EStavZadostAmlz.ROZP
                                                      };
                                                      // row.id + '' === profily?.id + '' && delete updateData.doplneno;
                                                      amlzC2osobyDetailsend(idZadosti, row.idosoby!, updateData, 'PUT');
                                                      setPersonData(updateData);
                                                  } else {
                                                      setPersonData(resp);
                                                  }
                                              })
                                              .finally(() => setLoading(false));
                                          OsobyC2data?.map((e, i) => e.idosoby === row.idosoby && setSubjIndex(i));
                                      }}
                                  >
                                      <TranslateText>
                                          {row.stav &&
                                          [
                                              EStavZadostAmlz.HOTOVO,
                                              EStavZadostAmlz.HOTOVOIIS,
                                              EStavZadostAmlz.CDCPKONTR,
                                              EStavZadostAmlz.AUTKONTROLA,
                                              EStavZadostAmlz.CDCPKEKONTR
                                          ].includes(row.stav)
                                              ? 'ZOBRAZIT'
                                              : row.stav === EStavZadostAmlz.UZIVDOPL
                                                ? 'ZKONTROLUJTE'
                                                : 'BUTTON_UPRAVIT'}
                                      </TranslateText>
                                  </AnchorComponent>
                              }
                              {(row.stav === EStavZadostAmlz.HOTOVO ||
                                  row.stav === EStavZadostAmlz.HOTOVOIIS ||
                                  row.stav === EStavZadostAmlz.CDCPKONTR ||
                                  row.stav === EStavZadostAmlz.AUTKONTROLA ||
                                  row.stav === EStavZadostAmlz.CDCPKEKONTR) && (
                                  <AnchorComponent
                                      style={{
                                          marginLeft: 14
                                      }}
                                      id={`c2UpravitBtn${row.id}`}
                                      key={`c2UpravitBtn${row.id}`}
                                      onClick={() => {
                                          setLoading(true);
                                          amlzC2osobyDetailGet(idZadosti, row.idosoby!)
                                              .then(resp => {
                                                  const updateData = {
                                                      ...resp,
                                                      stav: EStavZadostAmlz.ROZP
                                                  };
                                                  // row.id + '' === profily?.id + '' && delete updateData.doplneno;
                                                  amlzC2osobyDetailsend(idZadosti, row.idosoby!, updateData, 'PUT');
                                                  setPersonData(updateData);
                                              })
                                              .finally(() => setLoading(false));
                                          OsobyC2data?.map((e, i) => e.idosoby === row.idosoby && setSubjIndex(i));
                                      }}
                                  >
                                      <TranslateText>BUTTON_UPRAVIT</TranslateText>
                                  </AnchorComponent>
                              )}
                              {row.id + '' !== profily?.id + '' && (
                                  <AnchorComponent
                                      style={{
                                          marginLeft: 14
                                      }}
                                      key={`c2OderbratBtn${index}`}
                                      onClick={() => {
                                          confirm({
                                              title: <TranslateText>POTVRDENI</TranslateText>,
                                              content: `${getLanguageObject(language).DELETE_CONFIRM_C2_TEXT}`,
                                              cancelText: <TranslateText>NE</TranslateText>,
                                              okText: <TranslateText>ANO</TranslateText>,
                                              onOk() {
                                                  setLoading(true);
                                                  setSubjIndex(-1);
                                                  osobyC2Odebrat(row.idosoby!, row.idzadosti!)
                                                      .then(() => setRefetchC2(pv => !pv))
                                                      .finally(() => setLoading(false));
                                              }
                                          });
                                      }}
                                      id={`c2OderbratBtn${index}`}
                                  >
                                      <TranslateText>ODEBRAT</TranslateText>
                                  </AnchorComponent>
                              )}
                          </div>
                      )
                  }
              ])
    ];

    return !personData ? (
        <LoadingWrapper loading={loading}>
            <FormWrapper outForm={form} onFinish={onFinish} name="C2">
                <p>
                    <TranslateText>
                        {isLegalEntity
                            ? 'FORMULARE_ZASTUPUJUCE_OSOBY_TEXT_LEGALENTITY'
                            : 'FORMULARE_ZASTUPUJUCE_OSOBY_TEXT'}
                    </TranslateText>
                </p>
                <Table
                    loading={!C2data}
                    rowKey={(_, index) => index || 0}
                    /*  rowClassName={(_, i) =>
                        [...indexFailedFileValidation, ...indexFailedDateValidation].includes(i) ? 'tableRowRed' : ''
                    } */
                    locale={{
                        emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                    }}
                    style={{
                        margin: '30px 0px'
                    }}
                    columns={getColumns()}
                    dataSource={OsobyC2dataFiltered}
                    pagination={false}
                    rowClassName={row =>
                        (row.stav === EStavZadostAmlz.HOTOVO ||
                            row.stav === EStavZadostAmlz.HOTOVOIIS ||
                            row.stav === EStavZadostAmlz.CDCPKONTR ||
                            row.stav === EStavZadostAmlz.AUTKONTROLA ||
                            row.stav === EStavZadostAmlz.CDCPKEKONTR) &&
                        !row.duplicita
                            ? ''
                            : 'tableRowRed'
                    }
                    footer={() =>
                        !isLegalEntity && (
                            <AnchorComponent
                                id="c2PridatOsobuBtn"
                                style={{
                                    textAlign: 'center',
                                    display: 'block'
                                }}
                                onClick={() => {
                                    setLoading(true);
                                    amlzC2osobyPridat(idZadosti)
                                        .then(resp => {
                                            SubmitFunc('c2', {
                                                ...C2data,
                                                osoby: [
                                                    ...(C2data?.osoby || []),
                                                    {
                                                        id: resp.id
                                                    }
                                                ]
                                            });
                                            setSubjIndex((OsobyC2data || []).length);
                                            setPersonData({
                                                stav: EStavZadostAmlz.ROZP
                                            });
                                        })
                                        .finally(() => setLoading(false));
                                }}
                            >
                                <TranslateText>PRIDAT_OSOBU</TranslateText>
                            </AnchorComponent>
                        )
                    }
                />
                {!!OsobyC2pridatData?.length && !isLegalEntity && (
                    <>
                        <Row>
                            <TranslateText>FORMULARE_ZASTUPUJUCE_OSOBY_PRIDAT_TEXT</TranslateText>
                        </Row>
                        <Row>
                            <TranslateText>AMLZ_C2_ISIN_PRAVA</TranslateText>
                        </Row>
                        <Table
                            loading={!C2data}
                            rowKey={(_, index) => index || 0}
                            /*  rowClassName={(_, i) =>
                        [...indexFailedFileValidation, ...indexFailedDateValidation].includes(i) ? 'tableRowRed' : ''
                    } */
                            locale={{
                                emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                            }}
                            style={{
                                margin: '30px 0px'
                            }}
                            columns={getColumns(true)}
                            dataSource={OsobyC2pridatData}
                            pagination={false}
                        />
                    </>
                )}
                {c2ChybaVse && (
                    <Paragraph
                        style={{
                            display: 'block',
                            fontWeight: 600,
                            color: '#ff4d4f'
                        }}
                        text={isLegalEntity ? 'AMLZ_C6_DISABLED_C2DLT' : 'AMLZ_C6_DISABLED_C2'}
                    />
                )}

                {isC6edit ? (
                    <Row className="button-row">
                        <ButtonComponent
                            buttontext="POKRACOVAT_NA_ZHRNUTI"
                            type="primary"
                            onClick={() => form.submit()}
                            disabled={
                                C2data?.osoby
                                    ? C2data.osoby.length < 1 /* ||
                                      [...indexFailedFileValidation, ...indexFailedDateValidation].length > 0 */
                                    : true
                            }
                            //   loading={uploading}
                            id="C2PrehledBtn"
                        />
                    </Row>
                ) : (
                    <Row className="button-row">
                        <ButtonComponent buttontext="ZPET" onClick={() => prevStep()} id="C2ZpetBtn" type="default" />
                        <ButtonComponent
                            buttontext="POKRACOVAT"
                            id="C2PokracovatBtn"
                            disabled={
                                C2data?.osoby
                                    ? C2data.osoby.length < 1 /* ||
                                      [...indexFailedFileValidation, ...indexFailedDateValidation].length > 0 */
                                    : true
                            }
                            type="primary"
                            onClick={() => form.submit()}
                            //  loading={uploading}
                        />
                    </Row>
                )}
            </FormWrapper>
        </LoadingWrapper>
    ) : idOsoby ? (
        <LoadingWrapper loading={loading}>
            <AgendaAmlz
                isLegalEntity={isLegalEntity}
                setPdfCheckError={setPutResp}
                pdfCheckError={putResp}
                setSubmitPersonData={
                    setSubmitPersonData as React.Dispatch<React.SetStateAction<ISubjPravo | undefined>>
                }
                personData={personData as ISubjPravo}
                idZadosti={idZadosti}
                setPersonDataFinish={setPersonDataFinish}
                setshowInstrukceAfterFinish={setshowInstrukceAfterFinish}
                // putResp={putResp}
                idOsoby={idOsoby}
                mode={
                    personData.stav === EStavZadostAmlz.HOTOVO ||
                    personData.stav === EStavZadostAmlz.HOTOVOIIS ||
                    personData.stav === EStavZadostAmlz.CDCPKONTR ||
                    personData.stav === EStavZadostAmlz.AUTKONTROLA ||
                    personData.stav === EStavZadostAmlz.CDCPKEKONTR
                        ? 'displayOnly'
                        : 'C2'
                }
                setPersonData={setPersonData}
                apiList={apiListZadostC2}
                setRefetchTableData={setRefetchC2}
                isAmlzRequired={
                    (OsobyC2data?.length && subjIndex > -1
                        ? OsobyC2data[subjIndex].id === Number(profily?.id)
                        : false) || !!amlz?.goToC2Osoba
                }
                setIsSubmittingManually={setIsSubmittingManually}
            />
        </LoadingWrapper>
    ) : (
        <></>
    );
};

export default C2;
